import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { DateRangePicker } from '@/components/DateRangePicker'
import { DepartmentSelectField } from '@/components/departments/DepartmentSelectField'

export interface UserSearchParams {
	username?: string
	nickname?: string
	email?: string
	phone?: string
	department_id?: number
	status?: number
	is_super_admin?: number
	start_date?: string
	end_date?: string
}

interface UserSearchFormProps {
	value: UserSearchParams
	onChange: (value: UserSearchParams) => void
	onSearch: () => void
	onReset: () => void
}

export function UserSearchForm({ value: search, onChange, onSearch, onReset }: UserSearchFormProps) {
	const handleInputChange = (field: keyof UserSearchParams, inputValue: string) => {
		onChange({ ...search, [field]: inputValue })
	}

	const handleStatusChange = (statusValue: string) => {
		if (statusValue === 'all') {
			const nextValue = { ...search }
			delete nextValue.status
			onChange(nextValue)
		} else {
			onChange({ ...search, status: parseInt(statusValue, 10) })
		}
	}

	const handleIsSuperAdminChange = (value: string) => {
		if (value === 'all') {
			const nextSearch = { ...search }
			delete nextSearch.is_super_admin
			onChange(nextSearch)
		} else {
			onChange({ ...search, is_super_admin: parseInt(value, 10) })
		}
	}

	const handleDepartmentChange = (departmentId: number | null) => {
		if (departmentId === null) {
			const nextValue = { ...search }
			delete nextValue.department_id
			onChange(nextValue)
			return
		}
		onChange({ ...search, department_id: departmentId })
	}

	const handleDateChange = (dateValue: { start_date?: string; end_date?: string }) => {
		onChange({
			...search,
			start_date: dateValue.start_date,
			end_date: dateValue.end_date,
		})
	}

	const handleReset = () => {
		onChange({})
		onReset()
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			onSearch()
		}
	}

	return (
		<div className="space-y-4">
			{/* 第一行：文本输入框 */}
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
				<div>
					<Input
						placeholder="用户名"
						value={search.username || ''}
						onChange={(e) => handleInputChange('username', e.target.value)}
						onKeyDown={handleKeyDown}
					/>
				</div>
				<div>
					<Input
						placeholder="昵称"
						value={search.nickname || ''}
						onChange={(e) => handleInputChange('nickname', e.target.value)}
						onKeyDown={handleKeyDown}
					/>
				</div>
				<div>
					<Input
						placeholder="邮箱"
						value={search.email || ''}
						onChange={(e) => handleInputChange('email', e.target.value)}
						onKeyDown={handleKeyDown}
					/>
				</div>
				<div>
					<Input
						placeholder="手机号"
						value={search.phone || ''}
						onChange={(e) => handleInputChange('phone', e.target.value)}
						onKeyDown={handleKeyDown}
					/>
				</div>
			</div>

			{/* 第二行：筛选条件和按钮 */}
			<div className="flex flex-wrap items-center gap-4">
				<div className="w-full sm:w-[220px]">
					<DepartmentSelectField
						value={search.department_id ?? null}
						onChange={handleDepartmentChange}
					/>
				</div>

				<Select
					value={search.status !== undefined ? String(search.status) : 'all'}
					onValueChange={handleStatusChange}
				>
					<SelectTrigger className="w-[120px]">
						<SelectValue placeholder="状态" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">全部状态</SelectItem>
						<SelectItem value="1">正常</SelectItem>
						<SelectItem value="0">禁用</SelectItem>
					</SelectContent>
				</Select>

				<Select
					value={search.is_super_admin !== undefined ? String(search.is_super_admin) : 'all'}
					onValueChange={handleIsSuperAdminChange}
				>
					<SelectTrigger className="w-[140px]">
						<SelectValue placeholder="是否超管" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">全部</SelectItem>
						<SelectItem value="1">是</SelectItem>
						<SelectItem value="0">否</SelectItem>
					</SelectContent>
				</Select>

				<DateRangePicker
					value={{
						start_date: search.start_date,
						end_date: search.end_date,
					}}
					onChange={handleDateChange}
				/>

				<div className="flex gap-2">
					<Button onClick={onSearch}>搜索</Button>
					<Button variant="outline" onClick={handleReset}>
						重置
					</Button>
				</div>
			</div>
		</div>
	)
}
